import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DxValidationGroupComponent } from "devextreme-angular";
import notify from "devextreme/ui/notify";
import {
    IOcbAzureStorageRemotePlatform,
    IOcbSaveAzureStorageRemotePlatform,
    OcbAzureStorageAuthenticationModes,
    OcbAzureStorageServices,
    RemotePlatformsService
} from "../../core";
import { forkJoin } from "rxjs";

@Component({
    selector: 'app-remote-platform-azure-storage',
    templateUrl: './remote-platform-azure-storage-popup.component.html',
    styleUrls: ['./remote-platform-azure-storage-popup.component.scss']
})
export class AzureStoragePopupComponent {
    @Input() public platform?: IOcbAzureStorageRemotePlatform;
    @Input() public readOnly: boolean = false;
    public visible: boolean = false;

    @Output() public onClose: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('remotePlatformValidation')
    private remotePlatformValidationGroup: DxValidationGroupComponent;

    private authenticationModes: any[];
    private hasChanges: boolean = false;
    private isProgressBarVisible: boolean = false;
    private currentEnvironmentId: string = '';
    private placeHolderSetAccessKeyOrSasToken: string = '';
    private placeHolderSetAccessKeyOrSasTokenToChangeCurrent: string = '';
    private placeHolderSetSasTokenOrAccessKey: string = '';
    private placeHolderSetSasTokenOrAccessKeyToChangeCurrent: string = '';

    private getAccessKeyPlaceHolder() {
        if (this.platform.useAgentsEntraIdentity) return '';
        return this.platform.id ? this.placeHolderSetAccessKeyOrSasTokenToChangeCurrent : this.placeHolderSetAccessKeyOrSasToken;
    }

    private getNew(): IOcbAzureStorageRemotePlatform {
        return {
            environmentId: this.currentEnvironmentId,
            name: null,
            accountName: null,
            endpointSuffix: 'core.windows.net',
            accessKey: null,
            sasToken: null,
            serviceType: OcbAzureStorageServices.Blobs,
            downloadUnknownFiles: false,
            ipFiltering: false,
            isDisabled: false,
            useAgentsEntraIdentity: true
        };
    }

    private getSasTokenPlaceHolder() {
        if (this.platform.useAgentsEntraIdentity) return '';
        return this.platform.id ? this.placeHolderSetSasTokenOrAccessKeyToChangeCurrent : this.placeHolderSetSasTokenOrAccessKey;
    }

    private getSaveModel(): IOcbSaveAzureStorageRemotePlatform {
        return {
            environmentId: this.platform.environmentId,
            name: this.platform.name,
            endpointSuffix: this.platform.endpointSuffix,
            accountName: this.platform.accountName,
            accessKey: this.platform.accessKey,
            sasToken: this.platform.sasToken,
            serviceType: this.platform.serviceType,
            downloadUnknownFiles: this.platform.downloadUnknownFiles,
            ipFiltering: this.platform.ipFiltering,
            isDisabled: this.platform.isDisabled,
            useAgentsEntraIdentity: this.platform.useAgentsEntraIdentity
        } as IOcbSaveAzureStorageRemotePlatform;
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    constructor(private remotePlatformsService: RemotePlatformsService, private route: ActivatedRoute, private translateService: TranslateService) {
        this.getAccessKeyPlaceHolder = this.getAccessKeyPlaceHolder.bind(this);
        this.getAuthenticationModeLabel = this.getAuthenticationModeLabel.bind(this);
        this.getSasTokenPlaceHolder = this.getSasTokenPlaceHolder.bind(this);
        this.onServiceChanged = this.onServiceChanged.bind(this);
        this.save = this.save.bind(this);
        this.validateCredentials = this.validateCredentials.bind(this);
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.platform = this.getNew();
        });
        forkJoin([
            this.translateService.get('azureStorageAuthenticationModes'),
            this.translateService.get('remotePlatforms.lblSetAccessKeyOrSasToken'),
            this.translateService.get('remotePlatforms.lblSetAccessKeyOrSasTokenToChangeCurrent'),
            this.translateService.get('remotePlatforms.lblSetSasTokenOrAccessKey'),
            this.translateService.get('remotePlatforms.lblSetSasTokenOrAccessKeyToChangeCurrent'),
        ])
        .subscribe(messages => {
            this.authenticationModes = messages[0];
            this.placeHolderSetAccessKeyOrSasToken = messages[1];
            this.placeHolderSetAccessKeyOrSasTokenToChangeCurrent = messages[2];
            this.placeHolderSetSasTokenOrAccessKey = messages[3];
            this.placeHolderSetSasTokenOrAccessKeyToChangeCurrent = messages[4];
        });
    }

    private validateCredentials() {
        if (this.platform.useAgentsEntraIdentity) return true;
        return this.platform.id
            || (this.platform.accessKey || this.platform.sasToken);
    }

    public getAuthenticationModeLabel() {
        return this.authenticationModes[OcbAzureStorageAuthenticationModes[this.platform.authenticationMode]];
    }

    public onHidden() {
        this.platform = this.getNew();
        this.onClose.emit(this.hasChanges);
    }

    public onServiceChanged(e) {
        if (e == null) return;
        if (this.platform.serviceType === OcbAzureStorageServices.Files)
            this.platform.useAgentsEntraIdentity = false;
        else if (!this.platform?.id)
            this.platform.useAgentsEntraIdentity = true;
    }

    public onShown() { this.hasChanges = false; }

    private save() {
        const validationResult = this.remotePlatformValidationGroup.instance.validate();
        if (!validationResult.isValid) return;
        this.isProgressBarVisible = true;
        const saveModel = this.getSaveModel();
        if (!this.platform.id) {
            this.remotePlatformsService.addAzureStorageRemotePlatform(saveModel).subscribe({
                next: _ => {
                    this.isProgressBarVisible = false;
                    this.hasChanges = true;
                    this.visible = false;
                },
                error: (err) => this.handleError(err)
            });
        }
        else {
            this.remotePlatformsService.updateAzureStorageRemotePlatform(this.platform.id, saveModel).subscribe({
                next: _ => {
                    this.isProgressBarVisible = false;
                    this.hasChanges = true;
                    this.visible = false;
                },
                error: (err) => this.handleError(err)
            });
        }
    }
}